import { Link, useNavigate } from '@tanstack/react-router'
import { LogOut, Settings, Sparkles } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useLogoutMutation } from '@/features/auth/api'
import { useWalletQuery } from '@/features/wallet/api'
import { useAuthStore } from '@/stores/auth-store'

/**
 * Floating account cluster — pinned to the top-right corner of every
 * logged-in route. Keeps the dashboard's icon-tile aesthetic instead of
 * imposing a heavy horizontal nav bar. Balance + avatar; everything else
 * lives behind the avatar dropdown.
 */
export function AuthedHeader() {
  const user = useAuthStore((s) => s.user)
  const wallet = useWalletQuery()
  const logout = useLogoutMutation()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    function onDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [menuOpen])

  if (!user) return null

  const initials = (user.display_name || user.email)
    .split(/[\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('')

  return (
    <div
      className="fixed top-4 right-4 z-40 flex items-center gap-2"
      ref={menuRef}
    >
      <Link
        to="/top-up"
        className="flex h-10 items-center gap-1.5 rounded-full border border-border bg-card/80 px-3 text-sm font-medium text-foreground shadow-sm backdrop-blur transition-colors hover:bg-card"
        title="Credit balance — click to top up"
      >
        <span className="tabular-nums">
          {wallet.isLoading ? '…' : (wallet.data?.balance ?? 0)}
        </span>
        <span className="text-muted-foreground">GP</span>
      </Link>

      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex size-10 items-center justify-center rounded-full border border-border bg-card/80 shadow-sm backdrop-blur transition-colors hover:bg-card"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Account menu"
        >
          <Avatar className="size-8">
            <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
              {initials || '?'}
            </AvatarFallback>
          </Avatar>
        </button>

        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-[calc(100%+8px)] w-60 overflow-hidden rounded-2xl border border-border bg-popover text-popover-foreground shadow-lg"
          >
            <div className="border-b border-border px-4 py-3">
              <p className="truncate text-sm font-semibold text-foreground">
                {user.display_name || 'Member'}
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {user.email}
              </p>
            </div>
            <Link
              to="/dashboard"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted"
              role="menuitem"
            >
              <Sparkles className="size-4" />
              Dashboard
            </Link>
            <Link
              to="/settings"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted"
              role="menuitem"
            >
              <Settings className="size-4" />
              Settings
            </Link>
            <Link
              to="/pricing"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-muted"
              role="menuitem"
            >
              <Sparkles className="size-4" />
              Plans &amp; pricing
            </Link>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false)
                logout.mutate(undefined, {
                  onSettled: () => void navigate({ to: '/' }),
                })
              }}
              className="flex w-full items-center gap-2 border-t border-border px-4 py-2.5 text-left text-sm text-destructive hover:bg-destructive/5"
              role="menuitem"
            >
              <LogOut className="size-4" />
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
